import { all, fork, select, setContext, take } from 'redux-saga/effects';
import { reduxInitialized } from '../actions/initialization';
import {
  getPublicSession,
  getIsLoggedIn,
} from '@wix/da-shared-react/pkg/publicSession/selectors';
import { PublicSessionState } from '@wix/da-react-context/pkg/publicSession';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@wix/da-papi-types/lib/supabase';
import { requestPuppyAsync } from '@wix/da-http-client/pkg/async';
import { isApiError } from '@wix/da-http-client';
import { authSaga, sessionLostSaga } from './auth';
import { counterSaga } from './counter';
import { drawerSaga } from './drawer';
import { roomSaga } from './room';
import { globalSaga } from './global';
import { draftsSaga } from './drafts';
import { localStorageSaga } from './localStorage';
import { chatSoundSaga } from './sound';
import { settingsSaga } from './settings';
import { logoutSaga } from './logout';
import { openMessageForSaga } from './openMessageFor';
import { searchSaga } from './search';
import { hubSaga } from './hub';
import { getFlags } from '@wix/da-react-context/pkg/flags/redux/selectors';
import { roomReactionUsersSaga } from './room/room-reaction-users';
import { imageUploadSaga } from './imageUpload';
import { pageCleanupSaga } from './pageCleanup';
import { realtimeSaga } from './realtime';
import { Url } from '@wix/da-url';
import { Howl } from 'howler';
import { FlagsObject } from '@wix/da-react-context/pkg/flags/types';
import { realtimeConnections } from '../RealtimeConnections';
import ddt from '@wix/da-ddt';

/**
 * Storybook doesn't like redux modules, so we split the saga into two parts:
 * 1. This function that has all the saga functionality. We'd use this in storybook.
 * 2. {@link rootSaga} that gates saga execution behind the {@link reduxInitialized} action. We'd use that one
 *    in our apps.
 */
function* modulelessRootSaga() {
  const isLoggedIn: boolean = yield select(getIsLoggedIn);
  if (!isLoggedIn) {
    return;
  }
  // Create the client and set it in saga context for use in sub-sagas
  const session: PublicSessionState = yield select(getPublicSession);
  const damzConfig = session.damz;

  // Flags for realtime manager
  const flags: FlagsObject = yield select(getFlags);
  realtimeConnections.setFlags(flags);

  const conditionalFetch = async (
    url: string | URL | Request,
    init: RequestInit = {}
  ): Promise<Response> => {
    // Extract URL string from various input types
    let urlString: string;
    if (typeof url === 'string') {
      urlString = url;
    } else if (url instanceof URL) {
      urlString = url.href;
    } else {
      // Request object
      urlString = url.url;
    }
    const urlObject = new URL(urlString);

    // Authentication requests must go to Puppy.
    // Backend doesn't have anything to clean up on logout
    // so it's hardcoded to always succeed.
    if (urlObject.pathname === '/auth/v1/logout') {
      return new Response(null, {
        status: 204,
        statusText: 'No Content',
      });
    }

    if (urlObject.pathname.startsWith('/auth/v1/')) {
      try {
        const endpoint = urlObject.pathname.replace(/^\/auth\/v1/, '');

        // Convert URLSearchParams to plain object for axios
        const params: Record<string, string> = {};
        urlObject.searchParams.forEach((value, key) => {
          params[key] = value;
        });

        const getBodyData = async (body: BodyInit | null | undefined) => {
          if (!body) return undefined;
          if (typeof body === 'string') return JSON.parse(body);
          if (body instanceof Blob) return JSON.parse(await body.text());
          if (body instanceof ArrayBuffer)
            return JSON.parse(new TextDecoder().decode(body));
          return JSON.parse(String(body));
        };

        const convertHeaders = (
          headers: HeadersInit | undefined
        ): Record<string, string> | undefined => {
          if (!headers) return undefined;
          if (headers instanceof Headers) {
            const result: Record<string, string> = {};
            headers.forEach((value, key) => {
              result[key] = value;
            });
            return result;
          }
          if (Array.isArray(headers)) {
            return Object.fromEntries(headers);
          }
          return headers as Record<string, string>;
        };

        const response = await requestPuppyAsync(
          {
            url: `auth${endpoint}`,
            method: (init.method as any) ?? 'GET',
            data: await getBodyData(init.body),
            params: params,
            headers: convertHeaders(init.headers),
          },
          { csrfToken: session.csrfToken ?? '' },
          'damz'
        );

        // Wrap the response in a Response-like object that Supabase expects
        // requestPuppyAsync returns the axios response.data, which could be:
        // - The actual data (success case)
        // - false (500+ error case from getApiResponse)
        // - PapiError object (400-499 error case)
        const isError = response === false || isApiError(response);
        const status = response === false ? 500 : isError ? 400 : 200;

        // Normalize response data for JSON serialization
        // If response is false (server error), return a proper error object
        const responseData =
          response === false
            ? { error: 'Internal server error', errorCode: 'server_error' }
            : response;

        // Create a Response-like object
        // Supabase will call .json() on this to get the data
        return new Response(JSON.stringify(responseData), {
          status,
          statusText: status >= 400 ? 'Error' : 'OK',
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } catch (error: any) {
        // Handle network errors or other exceptions
        return new Response(
          JSON.stringify({ error: error.message || 'Network error' }),
          {
            status: 0,
            statusText: 'Network Error',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
      }
    } else {
      // For non-auth requests, use default fetch behavior
      // Supabase will wrap this with fetchWithAuth to add auth headers automatically
      return fetch(url, init);
    }
  };

  let supaClient: SupabaseClient<Database, 'damz'> | null = null;
  try {
    supaClient = createClient<Database, 'damz'>(
      damzConfig?.endpoint ?? '',
      damzConfig?.anonToken ?? '',
      {
        realtime: {
          worker: true,
          heartbeatIntervalMs: 15000,
        },
        db: {
          schema: 'damz',
        },
        global: {
          fetch: conditionalFetch,
        },
      }
    );
  } catch (error) {
    ddt.error('damz', 'Error creating supabase client', error);
    // Stop as without client we can't do anything
    return;
  }

  let howlerInstance: Howl | null = null;
  try {
    howlerInstance = new Howl({
      src: Url.staticCDNLink('eclipse/chat/chat-notification.mp3?2'),
      volume: 1.0,
    });
  } catch (error) {
    ddt.error('damz', 'Error creating howler instance', error);
  }

  // Add roomChannelTasks map to saga context
  yield setContext({
    supaClient,
    damzConfig,
    roomChannelTasks: new Map(),
    howlerInstance,
  });

  yield all([
    fork(authSaga), // This will set damz to online on success
    fork(sessionLostSaga), // Handle session loss and reconnection
    fork(localStorageSaga),
    fork(counterSaga),
    fork(globalSaga),
    fork(drawerSaga),
    fork(roomSaga),
    fork(draftsSaga),
    fork(chatSoundSaga),
    fork(settingsSaga),
    fork(logoutSaga),
    fork(openMessageForSaga),
    fork(searchSaga),
    fork(roomReactionUsersSaga),
    fork(imageUploadSaga),
    fork(pageCleanupSaga),
    fork(realtimeSaga),
    fork(hubSaga),
  ]);
}
export default function* rootSaga() {
  yield take(reduxInitialized);
  yield modulelessRootSaga();
}
